import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { DataListProps } from './types';
import DataListHeader from './DataListHeader';
import DataListRow from './DataListRow';
import DataListSkeleton from './DataListSkeleton';
import DataListEmpty from './DataListEmpty';

/**
 * DataList - A modern, premium-looking data table component
 * Features: sticky header, sticky action column on right, horizontal scroll, infinite scroll, dark mode support
 * Supports expandable rows for hierarchical data (e.g., Advanced Epics with child Features)
 */

const DataList = <T,>({
    data,
    columns,
    visibleColumns,
    columnWidths,
    loading = false,
    loadingMore = false,
    hasMore = false,
    onLoadMore,
    onRowClick,
    onSort,
    sortConfig,
    emptyMessage,
    getRowKey,
    scrollRef,
    skeletonRows = 8,
    className = '',
    headerClassName = '',
    rowClassName = '',
    rowHeight = 56,
    // Sticky column support
    stickyColumns = ['action'],
    // Expandable row props
    isRowExpandable,
    getChildRows,
    expandedRows,
    onToggleExpand,
    // Column customization
    onColumnOrderChange,
    onColumnVisibilityChange,
    onResetColumns,
    hiddenColumnKeys,
    fixedColumnKeys,
    externalScroll = false,
    childNotPossible = false,
    onRowHover,
}: DataListProps<T> & { externalScroll?: boolean }) => {
    const lastRowRef = useRef<HTMLTableRowElement>(null);
    const containerRef = scrollRef || useRef<HTMLDivElement>(null);
    const loadMoreCalledRef = useRef(false);
    const dataLengthRef = useRef(data.length);

    // Track data length changes to reset the loadMore guard
    useEffect(() => {
        if (data.length !== dataLengthRef.current) {
            dataLengthRef.current = data.length;
            loadMoreCalledRef.current = false;
        }
    }, [data.length]);

    // Stable onLoadMore callback that prevents duplicate calls
    const stableOnLoadMore = useCallback(() => {
        if (loadMoreCalledRef.current) return;
        loadMoreCalledRef.current = true;
        onLoadMore?.();
    }, [onLoadMore]);

    // Infinite scroll observer
    // IMPORTANT: Do NOT include `data` in deps - it causes the observer to re-fire
    // on every sort/re-render, which triggers onLoadMore and creates duplicate rows.
    // Instead we track data.length changes separately above.
    useEffect(() => {
        if (!hasMore || loadingMore || loading || !lastRowRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    stableOnLoadMore();
                }
            },
            {
                root: externalScroll ? null : containerRef.current,
                threshold: 0.1,
                rootMargin: '100px',
            },
        );

        observer.observe(lastRowRef.current);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, loadingMore, loading, stableOnLoadMore, externalScroll]);

    const visibleCols = columns.filter(col => visibleColumns[col.key]);

    // Separate visible columns into scrollable and sticky for colgroup
    const { scrollableVisibleCols, stickyVisibleCols } = useMemo(() => {
        const sticky = visibleCols.filter(col => stickyColumns.includes(col.key));
        const scrollable = visibleCols.filter(col => !stickyColumns.includes(col.key));
        return { scrollableVisibleCols: scrollable, stickyVisibleCols: sticky };
    }, [visibleCols, stickyColumns]);

    // Calculate minimum content width based on visible columns to ensure consistent scroll behavior
    // This prevents header jump between skeleton loading and data loaded states
    const minContentWidth = useMemo(() => {
        // Base width per column in pixels - generous spacing for readability
        const avgColumnWidth = 150;
        const visibleCount = visibleCols.length;
        // Ensure minimum width that triggers horizontal scroll consistently
        return Math.max(visibleCount * avgColumnWidth, 1200);
    }, [visibleCols.length]);

    /**
     * Render a row and its children if expanded
     */
    const renderRowWithChildren = (
        row: T,
        index: number,
        isChild: boolean = false,
        depth: number = 0,
    ) => {
        const rowId = getRowKey(row);
        const isExpandable = isRowExpandable?.(row) ?? false;
        const isExpanded = expandedRows?.has(rowId) ?? false;
        const childRows = isExpanded && getChildRows ? getChildRows(row) : [];
        const isLoadTrigger = !isChild && index === Math.max(0, data.length - 5);

        return (
            <React.Fragment key={rowId}>
                <DataListRow
                    ref={isLoadTrigger ? lastRowRef : null}
                    row={row}
                    index={index}
                    columns={columns}
                    visibleColumns={visibleColumns}
                    onClick={onRowClick}
                    isLast={
                        !isChild &&
                        index === data.length - 1 &&
                        !loadingMore &&
                        childRows.length === 0
                    }
                    className={rowClassName}
                    rowHeight={rowHeight}
                    isChild={isChild}
                    depth={depth}
                    isExpandable={isExpandable}
                    isExpanded={isExpanded}
                    onToggleExpand={() => onToggleExpand?.(rowId)}
                    getRowKey={getRowKey}
                    stickyColumns={stickyColumns}
                    childNotPossible={childNotPossible}
                    onRowHover={onRowHover}
                />

                {/* Render child rows if expanded */}
                {isExpanded && childRows.length > 0 && (
                    <>
                        {childRows.map((childRow, childIndex) =>
                            renderRowWithChildren(childRow, childIndex, true, depth + 1)
                        )}
                    </>
                )}
            </React.Fragment>
        );
    };

    return (
        <div
            ref={containerRef}
            className={`
                relative w-full ${externalScroll ? 'h-auto overflow-visible' : 'h-full overflow-auto'}
                bg-white dark:bg-surface-raised
                border border-slate-200 dark:border-gray-600/40
                rounded-xl shadow-sm
                ${className}
            `}
            style={{ scrollbarGutter: 'stable' }}
        >
            {/* Inner wrapper for horizontal scroll - uses calculated minimum width for consistency */}
            <div style={{ minWidth: `${minContentWidth}px` }}>
                <table className="w-full h-full border-collapse table-fixed">
                    {/* Colgroup - applies consistent column widths to skeleton, header, AND data rows */}
                    <colgroup>
                        {scrollableVisibleCols.map(col => (
                            <col key={col.key} style={{ width: columnWidths[col.key] || 'auto' }} />
                        ))}
                        {stickyVisibleCols.map(col => (
                            <col key={col.key} style={{ width: columnWidths[col.key] || 'auto' }} />
                        ))}
                    </colgroup>

                    {/* Header */}
                    <DataListHeader
                        columns={columns}
                        visibleColumns={visibleColumns}
                        onSort={onSort}
                        sortConfig={sortConfig}
                        className={headerClassName}
                        stickyColumns={stickyColumns}
                        onColumnOrderChange={onColumnOrderChange}
                        onColumnVisibilityChange={onColumnVisibilityChange}
                        onResetColumns={onResetColumns}
                        hiddenColumnKeys={hiddenColumnKeys}
                        fixedColumnKeys={fixedColumnKeys}
                    />

                    {/* Content */}
                    <tbody className="relative">
                        {/* Loading State */}
                        {loading && (
                            <DataListSkeleton
                                columns={columns}
                                visibleColumns={visibleColumns}
                                columnWidths={columnWidths}
                                rowCount={skeletonRows}
                                rowHeight={rowHeight}
                                stickyColumns={stickyColumns}
                            />
                        )}

                        {/* Empty State */}
                        {!loading && data.length === 0 && (
                            <DataListEmpty message={emptyMessage} />
                        )}

                        {/* Data Rows */}
                        {!loading && data.length > 0 && (
                            <>{data.map((row, index) => renderRowWithChildren(row, index))}</>
                        )}

                        {/* Loading More Indicator */}
                        {loadingMore && (
                            <DataListSkeleton
                                columns={columns}
                                visibleColumns={visibleColumns}
                                columnWidths={columnWidths}
                                rowCount={2}
                                rowHeight={rowHeight}
                                stickyColumns={stickyColumns}
                            />
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DataList;
